/**
 * A névtábla és a pult felső sorára kerülő CÉGNÉV — egyetlen helyen (#168).
 *
 * MIÉRT KÖZÖS. A cégnévnek ma **két forrása** van, és a terméken belül négy
 * felület olvassa: az ajtó (`doorCompanyName`), a Jegyek oszlopa, a nyomtatási
 * ZIP-export és a névtábla-XLSX. Ebből három ugyanabban a sorrendben olvassa
 * (`Company.name`, majd `Registration.companyName`), a negyedik — a
 * névtábla-XLSX `PARTICIPANT` ága — **fordítva**. A #167 mérése szerint ez ma
 * két élő soron látszana (a „Scenso" / „SCENSO DOO" és a „STUDIO PRESENT" /
 * „Studio Present" helyesírási változatok), és az egységesítés szándékosan a
 * 2026-09-24-i esemény UTÁNRA van halasztva, mert elmozdítaná valamelyik
 * felület mai kimenetét.
 *
 * Ez a modul tehát **nem** egységesít: a többségi, három felületen élő
 * sorrendet veszi át, hogy a pultnál nyomtatott névtábla ne nyisson ÖTÖDIK
 * szemantikát. Amit hozzátesz: egyetlen hely, ahol a sorrend le van írva.
 *
 * A #167 VARRATA. A #167 egy szabad szöveges „szervezet" mezőt vezet be a
 * jegyen, és a beírt érték nyer minden más forrás előtt — négylépcsős
 * sorrendet adva. Az a mező ma **nem létezik az adatbázisban**, ezért a lánc
 * itt a 2. lépésnél kezdődik. Amikor a #167 élesedik, **egy sor a `PRECEDENCE`
 * tömb elejére és egy mező a `BadgeCompanySources`-be** kerül; a hívók nem
 * mozdulnak. (Korábban ez a bekezdés „egyetlen sort" ígért — a típusmezőt
 * elfelejtette beleszámolni.)
 *
 * Miért nem írtuk meg előre: egy nem létező oszlopra olvasó ág sosem fut le,
 * tehát nem is mérhető — zölden álló, bizonyítatlan kód maradna az esemény
 * hetében.
 *
 * MIT AD MA. A #167 2026-09-14-i élő mérése szerint a Connect+Grow 2026
 * 176 jegyéből **egynek sincs** hiányzó cégneve, és 140-nek mindkét forrása ki
 * van töltve. A felső sor tehát a mai résztvevők MINDEGYIKÉNÉL kitöltött lesz;
 * a 3. lépés (üres) a jövőbeli vendégjegyeknek szól, amikből ma nulla van.
 */

/**
 * A cégnév két mai forrása a jegyen, ahogy a `GET /tickets` kiadja őket
 * (`tickets.service.ts` `include`-ja: `Company: { select: { id, name,
 * taxNumber } }` és `Registration: { select: { companyName } }`).
 *
 * Mindkettő elhagyható: a `Company` sor csak adószámmal jön létre, a
 * `Registration` pedig hiányzik a kézzel felvitt és az importált jegyekről.
 */
export interface BadgeCompanySources {
  /**
   * A #167 beírt „szervezet" mezője — a sorrend ELSŐ lépcsője.
   *
   * Ezt a szervező gépeli, jegyenként: az a név, amit a résztvevő a
   * MELLKASÁN visel. Azért előzi meg a `Company.name`-t, mert az a számlázási
   * azonosság (adószámmal), és a kettő gyakran eltér — egy delegált a saját
   * leányvállalatát mondja, nem a szerződő céget.
   */
  organizationName?: string | null;
  Company?: { name?: string | null } | null;
  Registration?: { companyName?: string | null } | null;
}

/**
 * A sorrend, kiolvasható alakban — **ez a modul lényege**.
 *
 * A #167 összefésülése óta az első lépcső a beírt „szervezet" mező, ahogy azt
 * ez a fájl előre jelezte. A hívókban semmi nem mozdult: mindkét exportált
 * függvény ebből az EGY tömbből olvas, tehát a lépcső hozzáadása egyetlen
 * helyen történt.
 */
const PRECEDENCE: ReadonlyArray<(sources: BadgeCompanySources) => string | null | undefined> = [
  (t) => t.organizationName,
  (t) => t.Company?.name,
  (t) => t.Registration?.companyName,
];

/**
 * A névtáblára kerülő cégnév, vagy üres sztring, ha egyik forrás sem ad nevet.
 *
 * A csupa-szóköz értéket üresnek veszi: a `Registration.companyName` szabad
 * szöveges mező, és egy szóközökből álló név a névtáblán egy üres, de helyet
 * foglaló felső sort adna — a név alatta pedig elcsúszna.
 */
export function badgeCompanyName(sources: BadgeCompanySources | null | undefined): string {
  if (!sources) return '';

  for (const read of PRECEDENCE) {
    const value = read(sources);
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return '';
}

/**
 * A jegyen szereplő ÖSSZES cégnév — a KERESÉSHEZ, nem a kiíráshoz.
 *
 * MIÉRT KÜLÖN FÜGGVÉNY, ÉS MIÉRT NEM A {@link badgeCompanyName}. Az a
 * sorrendből a GYŐZTEST adja — ez a helyes a névtáblán és az oszlopban, mert
 * oda egy név fér. A kereséshez viszont káros: ha a jegyhez tartozó
 * `Company.name` „Hegyi Kft", a jelentkezéskor beírt cégnév pedig
 * „Studio Present", akkor a delegált a pultnál a MÁSODIKAT fogja mondani — és
 * egy csak a győztesre illesztő kereső nulla találatot adna rá.
 *
 * Pontosan ez a hiba osztálya, amit a #116 a Jegyek oldalon javított: ott a
 * szerveroldali `where.OR` szándékosan MINDKÉT forrásra illeszt. A pult
 * keresője ugyanezt teszi, csak kliensoldalon.
 *
 * A #167 mezője ide is bekerült, mert a {@link badgeCompanyName}-mel KÖZÖS
 * `PRECEDENCE` tömbből olvas — egyetlen helyen kellett hozzáadni.
 */
export function badgeCompanyCandidates(sources: BadgeCompanySources | null | undefined): string[] {
  if (!sources) return [];

  const seen = new Set<string>();
  for (const read of PRECEDENCE) {
    const value = read(sources);
    if (typeof value === 'string' && value.trim() !== '') {
      seen.add(value.trim());
    }
  }
  return [...seen];
}
